import { IRowNode } from 'ag-grid-enterprise';
import { DataType } from 'src/app/pbir/dataModel/DataType';
import { VisualField } from '../../VisualField';
import { DataFieldTimeValueKind } from 'src/app/services/api.services';
import { isNullOrUndefined } from '../../../../shared/utils';

export namespace dataComparer {
  export function compare(a: any, b: any, dataType: DataType, dateTimeValueKind?: DataFieldTimeValueKind): number {
    return getComparer(dataType, dateTimeValueKind)(a, b);
  }

  export function getComparerForField(vf: VisualField): (a: any, b: any) => number {
    return getComparer(vf.dataType, vf.target?.usageHints?.timeValueKind);
  }

  export function getComparer(dataType: DataType, dateTimeValueKind?: DataFieldTimeValueKind): (a: any, b: any) => number {
    if (dateTimeValueKind) {
      return getComparerForDateSubField(dateTimeValueKind);
    }

    if (dataType.isNumeric)
      return numberComparer;

    if (dataType.isDateOrDateTime)
      return dateComparer;

    if (dataType.isTimeOnly)
      return timeComparer;

    return stringComparer;
  }

  function getComparerForDateSubField(dateTimeValueKind): (a: any, b: any) => number {
    switch (dateTimeValueKind) {
      case DataFieldTimeValueKind.DateTime:
        return dateComparer;

      case DataFieldTimeValueKind.Year:
      case DataFieldTimeValueKind.Month:
      case DataFieldTimeValueKind.Week:
      case DataFieldTimeValueKind.Day:
      case DataFieldTimeValueKind.Hour:
      case DataFieldTimeValueKind.Minute:
      case DataFieldTimeValueKind.Second:
      case DataFieldTimeValueKind.SpendPeriod:
        return numberComparer;

      case DataFieldTimeValueKind.Quarter:
        return quarterComparer;

      case DataFieldTimeValueKind.YearAndQuarter:
        return yearAndQuarterComparer;

      case DataFieldTimeValueKind.YearAndMonth:
      case DataFieldTimeValueKind.YearAndSpendPeriod:
        return yearAndMonthComparer;

      case DataFieldTimeValueKind.MonthName:
        return monthNameComparer;

      case DataFieldTimeValueKind.WeekDay:
        return weekDayComparer;

      case DataFieldTimeValueKind.Time:
        return timeComparer;

      /// IDKD
      case DataFieldTimeValueKind.TimeOfDay:
        return timeComparer;
    }
  }

  const nullComparison = (a: any, b: any): number | undefined => {
    if (isNullOrUndefined(a)) {
      if (isNullOrUndefined(b))
        return 0;

      return -1;
    }
    else if (isNullOrUndefined(b)) {
      return 1;
    }

    return undefined;
  };

  const nanComparison = (a: any, b: any): number | undefined => {
    if (isNaN(a)) {
      if (isNaN(b))
        return 0;

      return -1;
    }
    else if (isNaN(b)) {
      return 1;
    }

    return undefined;
  };

  export const stringComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    return String(a).localeCompare(String(b));
  };

  export const dateComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    return new Date(a).getTime() - new Date(b).getTime();
  };

  export const numberComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    d = nanComparison(a, b);
    if (d !== undefined)
      return d;

    return Number(a) - Number(b);
  };

  export const timeComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    let [aHour, aMinute, aSecond] = a?.split(":");
    let [bHour, bMinute, bSecond] = b?.split(":");
    
    if (aHour !== bHour)
      return numberComparer(aHour, bHour);

    if (aMinute !== bMinute)
      return numberComparer(aMinute, bMinute);

    return numberComparer(aSecond ?? 0, bSecond ?? 0);
  };

  export const quarterComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    let aQuarterNum = !!a ? String(a).slice(1) : null;
    let bQuarterNum = !!b ? String(b).slice(1) : null;

    return numberComparer(aQuarterNum, bQuarterNum);
  };

  export const yearAndQuarterComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    let [aYear, aQuarter] = a?.split("-");
    let [bYear, bQuarter] = b?.split("-");

    if (aYear !== bYear)
      return numberComparer(aYear, bYear);

    return quarterComparer(aQuarter, bQuarter);
  };

  export const yearAndMonthComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    let [aYear, aMonth] = a?.split("-");
    let [bYear, bMonth] = b?.split("-");
    
    if (aYear !== bYear)
      return numberComparer(aYear, bYear);

    return numberComparer(aMonth, bMonth);
  };

  export const weekDayComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    let indexA = !!a ? WEEKDAY_NAMES_ORDER.indexOf(String(a).toLowerCase().trim()) : -1;
    let indexB = !!b ? WEEKDAY_NAMES_ORDER.indexOf(String(b).toLowerCase().trim()) : -1;

    return numberComparer(indexA, indexB);
  };

  export const monthNameComparer = (a: any, b: any) => {
    let d = nullComparison(a, b);
    if (d !== undefined)
      return d;

    let indexA = !!a ? MONTH_NAMES_ORDER.indexOf(String(a).toLowerCase().trim()) : -1;
    let indexB = !!b ? MONTH_NAMES_ORDER.indexOf(String(b).toLowerCase().trim()) : -1;

    return numberComparer(indexA, indexB);
  };

  export function agGridComparer(vf: VisualField): (valueA, valueB) => number {
    let comparer = getComparerForField(vf);

    return (valueA, valueB) => comparer(valueA?.context?.raw, valueB?.context?.raw);
  }

  export function agGridComparerInferType(valueA: any, valueB: any, nodeA: IRowNode<any>, nodeB: IRowNode<any>): number {
    let vf = nodeA.data.rowGroup.vf as VisualField;
    let comparer = getComparerForField(vf);

    return comparer(valueA?.context?.raw, valueB?.context?.raw);
  }

  export const MONTH_NAMES_ORDER: string[] = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december"
  ];

  export const WEEKDAY_NAMES_ORDER: string[] = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday"
  ];
}
